"use client"

import { nativeSafeReload } from "@/lib/capacitor/nav"

// Rendered by the app's error boundaries when a caught error turns out to be
// deployment version skew (a stale JS chunk or an outdated Server Action) rather
// than a real bug — see lib/deploy/versionSkew.ts. This is the calm counterpart
// to the "Tuvimos un problema" crash card: it tells the operator a new version
// shipped and offers a single clear action.
//
// The CTA does a HARD reload on purpose. For skew, the React `reset()` an error
// boundary provides would re-render the same stale tree and re-throw (the chunk
// is still missing) — only a full reload re-fetches the current deployment. The
// POS cart is persisted to localStorage (hooks/useCart.ts), so the reload
// rehydrates an in-progress order instead of losing it.
//
// Strings are hardcoded (not next-intl): an error screen must never depend on
// the i18n provider having mounted — same stance as app/error.tsx and
// app/global-error.tsx.

export function VersionUpdateScreen({ dark = false }: { dark?: boolean }) {
  function update() {
    // nativeSafeReload, NO location.reload(): en el shell iOS (builds ≤ 1.0(5),
    // que son los que están distribuidos) un reload de documento completo
    // parado en /{slug}/pos se entrega a Safari — el operador tocaba
    // "Actualizar", se abría Safari o no pasaba nada, y la app seguía clavada
    // en esta misma pantalla. Y esta es la pantalla TERMINAL del estado
    // `skewed` de PinGate y de los error boundaries del POS y del KDS: sin
    // salida real, la tablet queda muerta. BuildWatcher ya usaba el helper;
    // esta pantalla no. Ver lib/capacitor/nav.ts.
    nativeSafeReload()
  }

  const fg = dark ? "#FFFFFF" : "var(--redy-charcoal)"
  const bg = dark ? "#0A0A0B" : "var(--redy-warm-white)"
  const sub = dark ? "rgba(255,255,255,0.7)" : "var(--redy-secondary)"

  return (
    <div
      className="flex min-h-screen flex-col items-center justify-center gap-6 px-6 py-10 text-center"
      style={{ background: bg, color: fg }}
    >
      <div className="w-full max-w-[460px]">
        <p
          className="text-[10.5px] font-bold uppercase tracking-[0.2em]"
          style={{ color: "var(--redy-red)" }}
        >
          Nueva versión
        </p>
        <h1
          className="mt-3 text-[28px] font-extrabold leading-[1.05] sm:text-[32px]"
          style={{
            fontFamily: "'Satoshi', 'Inter', system-ui, sans-serif",
            letterSpacing: "-0.02em",
          }}
        >
          Hay una versión{" "}
          <span
            style={{
              background: "var(--redy-gradient)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            nueva
          </span>
          .
        </h1>
        <p className="mx-auto mt-4 max-w-sm text-[15px]" style={{ color: sub }}>
          Toca actualizar para cargar la versión más reciente. Tu orden en curso
          está guardada.
        </p>
        <div className="mt-8 flex justify-center">
          <button
            type="button"
            onClick={update}
            className="rounded-2xl px-10 py-4 text-[16px] font-black text-white transition-all active:scale-[0.98]"
            style={{
              background: "var(--redy-gradient)",
              boxShadow: "0 8px 24px rgba(215,38,61,0.22)",
            }}
          >
            Actualizar
          </button>
        </div>
      </div>
    </div>
  )
}
